import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronDown, Star, Search, Plus, AlertTriangle, Settings, Archive, Check } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import QuickAddTaskModal from './QuickAddTaskModal';
import EmptyState from './EmptyState';
import { BUTTON } from './utils/uiTokens';
import VistaGeneralView, { VISTA_GENERAL_SLUG } from './VistaGeneralView';

/**
 * Shell del proyecto activo (/proyectos/:slug*).
 * Navbar nivel 2 con: volver, dot de color, switcher dropdown, tabs vista, nueva tarea.
 * El contenido lo renderiza el Outlet (board/lista/calendario).
 */
const ProyectoShell = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [proyecto, setProyecto] = useState(null);
  const [tareas, setTareas] = useState([]);
  const [misProyectos, setMisProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [switcherFiltro, setSwitcherFiltro] = useState('');
  const [switcherActiveIdx, setSwitcherActiveIdx] = useState(0);
  const switcherRef = useRef(null);
  const switcherListRef = useRef(null);

  // ----- Carga del proyecto y tareas -----

  const fetchProyecto = useCallback(async (slugRef) => {
    try {
      setError(null);
      const { data } = await axiosInstance.get(`/api/proyectos/${slugRef}`);
      setProyecto(data);
      return data;
    } catch (err) {
      console.error('Error cargando proyecto:', err);
      if (err.response?.status === 404) setError('El proyecto no existe.');
      else if (err.response?.status === 403) setError('No tienes acceso a este proyecto.');
      else setError('No pudimos cargar el proyecto. Intenta de nuevo.');
      return null;
    }
  }, []);

  const fetchTareas = useCallback(async (proyectoId) => {
    try {
      const { data } = await axiosInstance.get(`/api/proyectos/${proyectoId}/tareas`);
      setTareas(data || []);
    } catch (err) {
      console.error('Error cargando tareas:', err);
    }
  }, []);

  const fetchMisProyectos = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/api/proyectos');
      setMisProyectos(data || []);
    } catch (err) {
      console.error('Error cargando mis proyectos:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setProyecto(null);
    setTareas([]);
    (async () => {
      const p = await fetchProyecto(slug);
      if (p) await fetchTareas(p.id);
      setLoading(false);
    })();
  }, [slug, fetchProyecto, fetchTareas]);

  useEffect(() => {
    fetchMisProyectos();
  }, [fetchMisProyectos]);

  // Refrescar la lista de proyectos al volver la ventana al foco: si el usuario
  // creó un proyecto en otra pestaña/vista, queremos que aparezca en el switcher
  // sin requerir refresh manual.
  useEffect(() => {
    const onFocus = () => fetchMisProyectos();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchMisProyectos]);

  // Refrescar tareas (despues de mutaciones)
  const refetchTareas = useCallback(async () => {
    if (proyecto?.id) await fetchTareas(proyecto.id);
  }, [proyecto?.id, fetchTareas]);

  // Refrescar el detalle del proyecto (etiquetas, hitos, miembros, etc.)
  const refetchProyecto = useCallback(async () => {
    if (slug) await fetchProyecto(slug);
  }, [slug, fetchProyecto]);

  // Mutacion optimista de una tarea (drag-drop, edicion inline)
  const updateTareaLocal = useCallback((id, patch) => {
    setTareas((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  // Agregar tarea local (despues de crear)
  const addTareaLocal = useCallback((nueva) => {
    setTareas((prev) => [nueva, ...prev]);
  }, []);

  // ----- Switcher dropdown -----

  useEffect(() => {
    if (!switcherOpen) return;
    const onClick = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setSwitcherOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [switcherOpen]);

  const proyectosFiltrados = useMemo(() => {
    const q = switcherFiltro.trim().toLowerCase();
    if (!q) return misProyectos;
    return misProyectos.filter(
      (p) => p.nombre.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q)
    );
  }, [misProyectos, switcherFiltro]);

  // Reset del activeIndex cada vez que cambia el filtro (el item resaltado
  // puede haber salido del resultado tras tipear).
  useEffect(() => { setSwitcherActiveIdx(0); }, [switcherFiltro]);

  // Teclado: ↑↓ navega, Enter abre el resaltado, Esc cierra. Escuchamos solo
  // cuando el dropdown esta abierto; el input dentro maneja sus propias teclas
  // de edicion (texto/backspace) sin interferir.
  useEffect(() => {
    if (!switcherOpen) return;
    const onKey = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSwitcherActiveIdx((i) => Math.min(i + 1, proyectosFiltrados.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSwitcherActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        if (proyectosFiltrados.length === 0) return;
        e.preventDefault();
        const p = proyectosFiltrados[switcherActiveIdx] || proyectosFiltrados[0];
        if (!p) return;
        setSwitcherOpen(false);
        if (p.id !== proyecto?.id) navigate(`/proyectos/${p.slug}`);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setSwitcherOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [switcherOpen, proyectosFiltrados, switcherActiveIdx, proyecto?.id, navigate]);

  // Asegurar que el item activo siempre este visible en el viewport del dropdown.
  useEffect(() => {
    if (!switcherOpen || !switcherListRef.current) return;
    const el = switcherListRef.current.querySelector(`[data-switcher-idx="${switcherActiveIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [switcherActiveIdx, switcherOpen]);

  const handleToggleFavorito = async () => {
    if (!proyecto) return;
    try {
      const { data } = await axiosInstance.post(`/api/proyectos/${proyecto.id}/favorito`);
      setProyecto((p) => ({ ...p, es_favorito: data.favorito }));
    } catch (err) {
      console.error('Error toggle favorito:', err);
    }
  };

  // ----- Modal de Nueva tarea (boton del header) -----
  // Reemplaza el CustomEvent 'proyecto:quick-task' previo que solo escuchaba
  // el board. Ahora funciona desde cualquier sub-vista del proyecto.
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  const handleTareaCreada = useCallback((nueva) => {
    setShowQuickAdd(false);
    if (nueva) addTareaLocal(nueva);
  }, [addTareaLocal]);

  // ----- Render -----

  const tabBase = "relative px-3 py-1.5 text-sm font-medium tracking-tight transition-colors duration-150";
  const tabIdle = "text-slate-500 hover:text-slate-900";
  const tabActive = "text-slate-900";

  if (loading) {
    return (
      <div className="bg-[#FAFAFA] flex flex-col -m-4 md:-m-8 w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] h-[calc(100%+2rem)] md:h-[calc(100%+4rem)] overflow-hidden">
        {/* Skeleton del navbar */}
        <header className="flex-shrink-0 bg-white border-b border-slate-200/80">
          <div className="flex items-center justify-between h-[56px] px-6 skeleton">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-3 w-16 bg-slate-100 rounded" />
              <div className="h-5 w-px bg-slate-200" />
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-slate-200" />
                <div className="h-4 w-40 bg-slate-100 rounded" />
                <div className="h-3 w-10 bg-slate-100/70 rounded" />
              </div>
              <div className="w-4 h-4 rounded bg-slate-100" />
            </div>
            <div className="hidden md:flex items-center gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-4 w-16 bg-slate-100 rounded" />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-slate-100" />
              <div className="w-px h-5 bg-slate-200" />
              <div className="h-7 w-28 rounded-md bg-slate-200/80" />
            </div>
          </div>
        </header>
        {/* Skeleton del body */}
        <main className="flex-1 px-6 py-4 skeleton">
          <div className="h-4 w-32 bg-slate-100 rounded mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...Array(4)].map((_, col) => (
              <div key={col} className="bg-white border border-slate-200/70 rounded-lg p-3">
                <div className="h-3 w-20 bg-slate-100 rounded mb-3" />
                {[...Array(3)].map((_, row) => (
                  <div key={row} className="h-12 bg-slate-50 rounded mb-2" />
                ))}
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (error || !proyecto) {
    return (
      <div className="bg-[#FAFAFA] flex items-center justify-center px-6 -m-4 md:-m-8 w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] h-[calc(100%+2rem)] md:h-[calc(100%+4rem)]">
        <EmptyState
          icon={AlertTriangle}
          title={error || 'Proyecto no disponible'}
          variant="centered"
          action={
            <button
              onClick={() => navigate('/proyectos')}
              className={`${BUTTON.secondary} ${BUTTON.size.md}`}
            >
              <ArrowLeft size={14} />
              Volver a proyectos
            </button>
          }
        />
      </div>
    );
  }

  const color = proyecto.color || '#06b6d4';

  return (
    <div className="bg-[#FAFAFA] flex flex-col -m-4 md:-m-8 w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] h-[calc(100%+2rem)] md:h-[calc(100%+4rem)] overflow-hidden">
      {/* Navbar nivel 2 */}
      <header className="flex-shrink-0 bg-white border-b border-slate-200/80">
        <div className="flex items-center justify-between h-[56px] px-6">
          {/* Lado izquierdo: volver + switcher */}
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/proyectos"
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 transition-colors group"
            >
              <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
              <span className="hidden sm:inline">Proyectos</span>
            </Link>

            <div className="h-5 w-px bg-slate-200" />

            {/* Switcher: sin min-w-0 / flex-1 / w-full → el wrapper toma el ancho natural
                del contenido (nombre completo). max-w-[500px] + overflow-hidden actúan de
                tope para nombres extremos. */}
            <div className="relative shrink-0 max-w-[500px] overflow-hidden" ref={switcherRef}>
              <button
                onClick={() => {
                  const next = !switcherOpen;
                  setSwitcherOpen(next);
                  setSwitcherFiltro('');
                  setSwitcherActiveIdx(0);
                  if (next) fetchMisProyectos(); // refrescar al abrir
                }}
                className="flex items-center gap-2 px-2 py-1 -mx-2 -my-1 rounded-md hover:bg-slate-100 transition-colors whitespace-nowrap"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color, boxShadow: `0 0 0 3px ${color}1a` }}
                />
                <span className="text-sm font-semibold tracking-tight text-slate-900">
                  {proyecto.nombre}
                </span>
                <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase flex-shrink-0">
                  {proyecto.slug}
                </span>
                {proyecto.archivado && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 flex-shrink-0">
                    <Archive size={9} strokeWidth={2.5} />
                    Archivado
                  </span>
                )}
                <ChevronDown
                  size={14}
                  className={`text-slate-400 flex-shrink-0 transition-transform ${switcherOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {switcherOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.97 }}
                    transition={{ duration: 0.12 }}
                    className="absolute left-0 top-full mt-1 w-72 bg-white rounded-lg shadow-xl ring-1 ring-slate-200 overflow-hidden z-30"
                  >
                    <div className="relative px-2 pt-2">
                      <Search size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        autoFocus
                        value={switcherFiltro}
                        onChange={(e) => setSwitcherFiltro(e.target.value)}
                        placeholder="Saltar a proyecto..."
                        className="w-full pl-7 pr-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-md placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                      />
                    </div>
                    <div ref={switcherListRef} className="max-h-72 overflow-y-auto py-1 mt-1">
                      {proyectosFiltrados.length === 0 ? (
                        <div className="px-3 py-3 text-xs text-slate-400 text-center">
                          Sin resultados
                        </div>
                      ) : (
                        proyectosFiltrados.map((p, idx) => {
                          const activo = p.id === proyecto.id;
                          const resaltado = idx === switcherActiveIdx;
                          return (
                            <button
                              key={p.id}
                              data-switcher-idx={idx}
                              onMouseEnter={() => setSwitcherActiveIdx(idx)}
                              onMouseDown={() => {
                                setSwitcherOpen(false);
                                if (!activo) navigate(`/proyectos/${p.slug}`);
                              }}
                              className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors ${
                                activo
                                  ? 'bg-cyan-50'
                                  : resaltado
                                  ? 'bg-slate-100'
                                  : 'hover:bg-slate-50'
                              }`}
                            >
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: p.color || '#06b6d4' }}
                              />
                              <span className={`text-sm truncate flex-1 ${activo ? 'text-cyan-700 font-medium' : 'text-slate-800'}`}>{p.nombre}</span>
                              <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase">
                                {p.slug}
                              </span>
                              {activo && (
                                <Check size={12} className="text-cyan-600 flex-shrink-0" strokeWidth={2.5} />
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={handleToggleFavorito}
              className="text-slate-300 hover:text-amber-400 transition-colors"
              title={proyecto.es_favorito ? 'Quitar de favoritos' : 'Marcar como favorito'}
            >
              <motion.span
                key={proyecto.es_favorito ? 'on' : 'off'}
                initial={proyecto.es_favorito ? { scale: 0.6 } : { scale: 1 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 600, damping: 14 }}
                className="inline-flex"
              >
                <Star
                  size={15}
                  strokeWidth={2}
                  className={proyecto.es_favorito ? 'fill-amber-400 text-amber-400' : ''}
                />
              </motion.span>
            </button>
          </div>

          {/* Centro: tabs de vista */}
          <nav className="hidden md:flex items-center gap-1 -mb-px">
            <NavLink
              to={`/proyectos/${proyecto.slug}`}
              end
              className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
            >
              {({ isActive }) => (
                <>
                  Tablero
                  {isActive && (
                    <motion.div
                      layoutId="proyecto-tab-underline"
                      className="absolute left-3 right-3 -bottom-px h-[2px] rounded-full"
                      style={{ backgroundColor: color }}
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>
            <NavLink
              to={`/proyectos/${proyecto.slug}/lista`}
              className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
            >
              {({ isActive }) => (
                <>
                  Lista
                  {isActive && (
                    <motion.div
                      layoutId="proyecto-tab-underline"
                      className="absolute left-3 right-3 -bottom-px h-[2px] rounded-full"
                      style={{ backgroundColor: color }}
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>
            <NavLink
              to={`/proyectos/${proyecto.slug}/calendario`}
              className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
            >
              {({ isActive }) => (
                <>
                  Calendario
                  {isActive && (
                    <motion.div
                      layoutId="proyecto-tab-underline"
                      className="absolute left-3 right-3 -bottom-px h-[2px] rounded-full"
                      style={{ backgroundColor: color }}
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>
            <NavLink
              to={`/proyectos/${proyecto.slug}/gantt`}
              className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
            >
              {({ isActive }) => (
                <>
                  Gantt
                  {isActive && (
                    <motion.div
                      layoutId="proyecto-tab-underline"
                      className="absolute left-3 right-3 -bottom-px h-[2px] rounded-full"
                      style={{ backgroundColor: color }}
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>
            {/* Red PERT/CPM + análisis (ruta crítica) */}
            <NavLink
              to={`/proyectos/${proyecto.slug}/red`}
              className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
            >
              {({ isActive }) => (
                <>
                  Red
                  {isActive && (
                    <motion.div
                      layoutId="proyecto-tab-underline"
                      className="absolute left-3 right-3 -bottom-px h-[2px] rounded-full"
                      style={{ backgroundColor: color }}
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          </nav>

          {/* Lado derecho: configuracion + nueva tarea */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate(`/proyectos/${proyecto.slug}/configuracion`)}
              title="Configuración del proyecto"
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
            >
              <Settings size={15} />
            </button>
            <div className="w-px h-5 bg-slate-200 mx-1.5" />
            <button
              onClick={() => setShowQuickAdd(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-md transition-colors active:scale-[0.98]"
            >
              <Plus size={14} strokeWidth={2.5} />
              Nueva tarea
            </button>
          </div>
        </div>
      </header>

      {/* Contenido */}
      <main className="flex-1 overflow-hidden">
        <Outlet
          context={{
            proyecto,
            tareas,
            refetchTareas,
            refetchProyecto,
            updateTareaLocal,
            addTareaLocal,
            color,
          }}
        />
      </main>

      <QuickAddTaskModal
        isOpen={showQuickAdd}
        proyecto={proyecto}
        onClose={() => setShowQuickAdd(false)}
        onCreated={handleTareaCreada}
      />
    </div>
  );
};

/**
 * /proyectos/:slug resuelve dos cosas: el shell de un proyecto real y, cuando el
 * slug es el reservado `vista-general`, el shell agregado de todos los proyectos.
 * Tenerlos bajo la misma ruta deja intacta la navegacion de las vistas hijas
 * (tablero, lista, calendario, gantt) y sus URLs de slide-over.
 */
const ProyectoView = () => {
  const { slug } = useParams();
  if (slug === VISTA_GENERAL_SLUG) return <VistaGeneralView />;
  return <ProyectoShell />;
};

export default ProyectoView;
